import { useEffect, useRef, useState, type ReactElement } from "react";

// The polyfill MUST load before react-pdf — pdf.js needs it at import time.
import "../lib/promise-with-resolvers";
import { Document, Page, pdfjs } from "react-pdf";

// pdf.js renders in a worker. Vite bundles this file as a same-origin asset,
// which the CSP already allows (worker-src falls back to default-src 'self').
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url,
).toString();

// Inline all-pages PDF viewer. Replaces <iframe className="consent-pdf">:
// mobile WebKit draws a PDF iframe as a static first-page preview with no
// scrollbar, so multi-page consent sheets looked one page long on phones.
// pdf.js paints every page onto canvases inside a normal scrollable div,
// which scrolls the same on every browser.
export function PdfViewer({ file, title }: { file: string | Blob; title: string }): ReactElement {
  const boxRef = useRef<HTMLDivElement>(null);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [width, setWidth] = useState<number | null>(null);

  // Fit pages to the container; re-measure on window resize (rotation).
  useEffect(() => {
    const measure = (): void => {
      if (boxRef.current) setWidth(boxRef.current.clientWidth);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  return (
    <div ref={boxRef} className="consent-pdf" title={title}>
      <Document
        file={file}
        onLoadSuccess={({ numPages: n }) => setNumPages(n)}
        loading={<p className="muted">Loading the document…</p>}
        error={
          <p className="form-error">
            Could not display the document here. Use the Download link above to open it.
          </p>
        }
      >
        {width !== null && numPages !== null
          ? Array.from({ length: numPages }, (_, i) => (
              <Page
                key={i + 1}
                pageNumber={i + 1}
                width={width}
                renderTextLayer={false}
                renderAnnotationLayer={false}
              />
            ))
          : null}
      </Document>
    </div>
  );
}
